import type { CodexQuotaData, QuotaResult } from '../types.ts';
import { getCodexAuth } from '../auth.ts';

interface CodexRateLimitHeaders {
  usedPercent: number | null;
  windowMinutes: number | null;
  resetAt: number | null;
  resetAfterSeconds: number | null;
}

const parseRateLimitHeaders = (headers: Headers): CodexRateLimitHeaders => {
  const usedPercent = headers.get('x-codex-primary-used-percent');
  const windowMinutes = headers.get('x-codex-primary-window-minutes');
  const resetAt = headers.get('x-codex-primary-reset-at');
  const resetAfterSeconds = headers.get('x-codex-primary-reset-after-seconds');

  return {
    usedPercent: usedPercent ? Number.parseInt(usedPercent, 10) : null,
    windowMinutes: windowMinutes ? Number.parseInt(windowMinutes, 10) : null,
    resetAt: resetAt ? Number.parseInt(resetAt, 10) : null,
    resetAfterSeconds: resetAfterSeconds ? Number.parseInt(resetAfterSeconds, 10) : null,
  };
};

const resolveResetAt = (limits: CodexRateLimitHeaders): number | null => {
  if (limits.resetAt) {
    return limits.resetAt;
  }

  if (limits.resetAfterSeconds) {
    const resetMs = Date.now() + limits.resetAfterSeconds * 1000;
    return Math.floor(resetMs / 1000);
  }

  return null;
};

export const fetchCodexQuota = async (): Promise<QuotaResult> => {
  const auth = await getCodexAuth();

  if (!auth) {
    return {
      provider: 'codex',
      ok: false,
      configured: false,
      summary: 'Not configured - no OAuth token found',
      data: null,
    };
  }

  const accessToken = auth.access ?? auth.token;
  if (!accessToken) {
    return {
      provider: 'codex',
      ok: false,
      configured: false,
      summary: 'Not configured - access token missing',
      data: null,
    };
  }

  try {
    const response = await fetch('https://api.openai.com/v1/models', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    const rateLimits = parseRateLimitHeaders(response.headers);
    if (rateLimits.usedPercent !== null) {
      const resetAt = resolveResetAt(rateLimits);
      const data: CodexQuotaData = {
        usedPercent: rateLimits.usedPercent,
        remainingPercent: 100 - rateLimits.usedPercent,
        windowMinutes: rateLimits.windowMinutes ?? 300,
        resetAt,
        planType: null,
      };

      const resetInfo = resetAt ? ` (resets ${new Date(resetAt * 1000).toLocaleTimeString()})` : '';

      return {
        provider: 'codex',
        ok: true,
        configured: true,
        summary: `${data.remainingPercent}% remaining${resetInfo}`,
        data,
      };
    }

    if (response.ok) {
      const data: CodexQuotaData = {
        usedPercent: 0,
        remainingPercent: 100,
        windowMinutes: 300,
        resetAt: null,
        planType: null,
      };

      return {
        provider: 'codex',
        ok: true,
        configured: true,
        summary: 'Authenticated (no usage data available)',
        data,
      };
    }

    return {
      provider: 'codex',
      ok: false,
      configured: true,
      summary: `API error: ${response.status}`,
      error: await response.text(),
      data: null,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      provider: 'codex',
      ok: false,
      configured: true,
      summary: `Request failed: ${message}`,
      error: message,
      data: null,
    };
  }
};
